import React from 'react';
import { useIntercom } from 'react-use-intercom';
import { Box, useTheme } from '@mui/material';
import { getPreApproveConditions, postPreApproveConditions } from '../../queries/authorization';
import { PreLoanRequestStep, LoanRequest } from '../../types/authorization';
import analytics from '../../utils/analytics';
import BancameBackdrop from '../../components/BancameBackdrop';
import AcceptConditions from './AcceptConditions';
import BancameWidget from '../../components/BancameWidget';
import BackgroundPic from '../../assets/background_forms_light.svg';
import LoanRequestStatus from './LoanRequestStatus';
import jwtDecode from '../../utils/jwtDecode';

type AuthorizationFormIndexProps = {
  token: string,
}

export default function AuthorizationFormIndex(props: AuthorizationFormIndexProps) {
  const {
    token,
  } = props;
  const theme = useTheme();
  const {
    boot: bootIntercom,
  } = useIntercom();
  const {
    origin, preApproveId, identificationId, email, hasAccount, productType,
  } = jwtDecode(token);

  const [errorCode, setErrorCode] = React.useState<number | null>(null);
  const [initialStep, setInitialStep] = React.useState<PreLoanRequestStep>('ARCHIVED_FAIL');
  const [loanRequest, setLoanRequest] = React.useState<LoanRequest | undefined>(undefined);
  const [widgetToken, setWidgetToken] = React.useState<string | null>(null);
  const [publicKey, setPublicKey] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [key, setKey] = React.useState(0);

  const validatePreApprove = async (isFirstLoad: boolean = false) => {
    try {
      setLoading(true);
      const response = await getPreApproveConditions(token);
      if (response.data.type === 'pre-approve') {
        throw new Error('Pre-approve not supported');
      }
      setInitialStep(response.data.plr.step);
      setLoanRequest(response.data.detail);
      analytics.identify(identificationId, {
        preApproveId,
        origin,
        loanRequestId: response.data.detail.id,
      });
    } catch (e) {
      if (isFirstLoad) {
        analytics.identify(identificationId, {
          preApproveId,
          origin,
          loanRequestId: undefined,
        });
      }
      setErrorCode(e as number);
    } finally {
      setLoading(false);
    }
  };

  const acceptConditionsSuccess = async () => {
    try {
      setLoading(true);
      const response = await postPreApproveConditions(token);
      setWidgetToken(response.data.widgetToken);
      setPublicKey(response.data.publicKey);
    } catch (e) {
      setErrorCode(e as number);
    } finally {
      setLoading(false);
    }
  };

  const updateLoanRequest = (updatedLoanRequest: LoanRequest) => {
    setLoanRequest(updatedLoanRequest);
  };

  React.useEffect(() => {
    if (token) {
      validatePreApprove(true);
      bootIntercom();
    }
  }, [token]);

  React.useEffect(() => {
    if (success) {
      validatePreApprove();
    }
  }, [success]);

  React.useEffect(() => {
    setKey(new Date().getTime());
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      padding: {
        xs: 1,
        sm: 2,
        md: 4,
        lg: 6,
      },
      width: '100%',
      backgroundColor: theme.palette.background.defaultSecondary,
      backgroundImage: `url(${BackgroundPic})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    }}
    >
      <BancameBackdrop openBackdrop={loading} loadingText="Validando pre-aprobación..." type="loading" />
      {token && (
        <>
          {errorCode && (
            <LoanRequestStatus
              errorCode={errorCode}
              origin={origin}
              success={success}
              email={email}
              token={token}
              hasAccount={hasAccount}
              productType={productType}
            />
          )}
          {!errorCode && ['ACCEPT_CONDITIONS', 'AUTHORIZATION', 'PAYMENT_SUBSCRIPTION'].includes(initialStep) && loanRequest && (
            <AcceptConditions
              token={token}
              loanRequest={loanRequest}
              onSuccess={acceptConditionsSuccess}
              onError={setErrorCode}
              onUpdateLoanRequest={updateLoanRequest}
              initialStep={initialStep as 'ACCEPT_CONDITIONS'| 'AUTHORIZATION'| 'PAYMENT_SUBSCRIPTION'}
              productType={productType}
            />
          )}
          {!errorCode && widgetToken && publicKey && (
            <BancameWidget
              key={key}
              widgetToken={widgetToken}
              publicKey={publicKey}
              onSuccess={() => {
                setSuccess(true);
              }}
              onReject={(event, metadata) => {
                if (event === 'error_callback') {
                  if (metadata.reason === 'TOKEN_EXPIRED') {
                    return setErrorCode(307);
                  }
                }
                return setErrorCode(500);
              }}
            />
          )}
        </>
      )}
    </Box>
  );
}
